/**
 * Navigation config for the VOLUNTEER_COMMUNITY logged-in shell
 * (`/community-dashboard/*`) — a VOLUNTEER-PERSON-CENTRIC dashboard. The unit of
 * focus is the individual volunteer: their activity, opportunities, learning,
 * badges/certificates, profile and community. The community is one section
 * ("Volunteer Community"), not the whole shell. No financial destinations.
 *
 * Two surfaces consume this:
 *  - COMMUNITY_SIDEBAR_SECTIONS → the 12-item LEFT sidebar (desktop) + mobile drawer
 *  - COMMUNITY_MOBILE_NAV       → the 5-item mobile bottom nav
 *
 * Labels are i18n keys under `nav.community.*` (resolved via `t('nav.community.<key>')`).
 * Reuses the `AppNavItem`/`AppNavSection` shapes from `appNav.ts` so the sidebar
 * renderer stays uniform. `leaderOnly` items are hidden from members who are not a
 * LEADER / COORDINATOR / MODERATOR of their community (gated by CommunityContext).
 *
 * Sub-routes from the old community-entity shell (feed, announcements, members,
 * campaigns, profile, activities) are retained as routes reachable from the
 * "Volunteer Community" hub; they are no longer top-level sidebar items.
 */
import {
  LayoutDashboard, Compass, ListChecks, GraduationCap, Users, FolderOpen,
  Award, ScrollText, UserCircle, Bell, Bot, Settings, type LucideIcon,
} from 'lucide-react'
import type { AppNavItem, AppNavSection } from './appNav'

/** Extends AppNavItem with an optional leader/moderator gate. */
export interface CommunityNavItem extends AppNavItem {
  leaderOnly?: boolean
}

export interface CommunityNavSection extends AppNavSection {
  items: CommunityNavItem[]
}

// ──────────────────────────────────────────────────────────────────────────
// SIDEBAR — 12 items per the volunteer-dashboard spec (§13):
// Dashboard / Volunteer Opportunities / My Activities / Learning Centre /
// Volunteer Community / Resources / Badges / Certificates / My Profile /
// Notifications / TB Assistant / Settings.
// ──────────────────────────────────────────────────────────────────────────
const COMMUNITY_ITEMS: CommunityNavItem[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/community-dashboard', icon: LayoutDashboard },
  { key: 'opportunities', label: 'Volunteer Opportunities', path: '/community-dashboard/opportunities', icon: Compass },
  { key: 'myActivities', label: 'My Activities', path: '/community-dashboard/my-activities', icon: ListChecks },
  { key: 'learning', label: 'Learning Centre', path: '/community-dashboard/learning', icon: GraduationCap },
  { key: 'community', label: 'Volunteer Community', path: '/community-dashboard/community', icon: Users },
  { key: 'resources', label: 'Resources', path: '/community-dashboard/resources', icon: FolderOpen },
  { key: 'badges', label: 'Badges', path: '/community-dashboard/badges', icon: Award },
  { key: 'certificates', label: 'Certificates', path: '/community-dashboard/certificates', icon: ScrollText },
  { key: 'myProfile', label: 'My Profile', path: '/community-dashboard/my-profile', icon: UserCircle },
  { key: 'notifications', label: 'Notifications', path: '/notifications', icon: Bell },
  { key: 'settings', label: 'Settings', path: '/community-dashboard/settings', icon: Settings, leaderOnly: true },
]

const ASSISTANT_ITEMS: CommunityNavItem[] = [
  { key: 'assistant', label: 'TB Assistant', path: '/assistant', icon: Bot },
]

/** LEFT sidebar sections (desktop) / mobile drawer. */
export const COMMUNITY_SIDEBAR_SECTIONS: CommunityNavSection[] = [
  { titleKey: 'nav.community.sectionCommunity', items: COMMUNITY_ITEMS },
  { titleKey: 'nav.community.sectionSupport', items: ASSISTANT_ITEMS, bottom: true },
]

// ──────────────────────────────────────────────────────────────────────────
// MOBILE BOTTOM NAV — 5 items (thumb-reachable), per spec §14:
// Home / Opportunities / My Activities / Community / My Profile.
// ──────────────────────────────────────────────────────────────────────────
export const COMMUNITY_MOBILE_NAV: CommunityNavItem[] = [
  { key: 'dashboard', label: 'Home', path: '/community-dashboard', icon: LayoutDashboard },
  { key: 'opportunities', label: 'Opportunities', path: '/community-dashboard/opportunities', icon: Compass },
  { key: 'myActivities', label: 'Activities', path: '/community-dashboard/my-activities', icon: ListChecks },
  { key: 'community', label: 'Community', path: '/community-dashboard/community', icon: Users },
  { key: 'myProfile', label: 'Profile', path: '/community-dashboard/my-profile', icon: UserCircle },
]

/** Resolve an item's path (no username substitution needed for community routes). */
export function resolveCommunityPath(item: CommunityNavItem, _username?: string | null): string {
  return item.path
}

/** The lucide icon type, re-exported for renderers. */
export type { LucideIcon }